/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Variant-aware row wrapper for the chat surface. Dispatches a
// single `Message` to the correct presentational primitive based on
// an optional `ChatMessageVariant` sidecar supplied by the host:
//
//   - "voice"  → <VoiceMessageBubble />        (Figma 3412:4708 / 4981)
//   - "image"  → <ImageAttachmentCard />       (Figma 3412:5288 / 5294)
//   - "doc"    → <DocAttachmentCard />         (Figma 3412:4897 / 4903)
//   - "event"  → <EventMessage />              (Figma 3412:5333)
//   - "edit"   → <EditMessageBubble />         (Figma 2869:5218)
//   - (none)   → <MessageRow /> (plain text)   (Figma 3412:5136 / 5207)
//
// The dispatch keeps `MessageBubble.tsx` untouched — only this row
// wrapper is updated. The variant payload itself is host-computed
// (the host inspects the MLS envelope `kind` and looks up the
// per-message playback / upload state).
//
// Reply chip on a received bubble is handled inside `MessageBubble`
// via `message.inReplyTo` and is therefore unchanged here. The
// composer-side reply quote (sender → composer) is rendered by
// `ChatRoomComposerRich` from props on `ChatRoom`.

import * as React from "react";
import { Pressable, View } from "react-native";
import type { Message } from "../sync/persistence";
import { Avatar } from "../components/Avatar";
import { MessageBubble } from "../chat/MessageBubble";
import { DocAttachmentCard } from "../chat/attachments/DocAttachmentCard";
import { ImageAttachmentCard } from "../chat/attachments/ImageAttachmentCard";
import { VideoAttachmentCard } from "../chat/attachments/VideoAttachmentCard";
import { LinkPreviewCard } from "../chat/attachments/LinkPreviewCard";
import { VoiceMessageBubble } from "../chat/VoiceMessageBubble";
import { EditMessageBubble } from "../chat/EditMessageBubble";
import { EventMessage } from "../chat/EventMessage";
import { MessageHoverHost } from "../chat/MessageHoverHost";
import { ThreadPreviewCard } from "../chat/ThreadPreviewCard";
import { useColorScheme } from "../foundations/colorScheme";
import { StackedMessageRow } from "./StackedMessageRow";
import { LocationAttachmentCard } from "../chat/attachments/LocationAttachmentCard";
import { GameTile } from "../chat/games/GameTile";
import type { GameSession } from "../sync/useGameSessions";
import { PollView } from "../polls/PollView";
import { MeetingView } from "../meetings/MeetingView";
import { CallNotificationCard } from "./CallNotificationCard";
import { formatClockTime } from "./messageFormatters";
import { ReactionsRow, type ReactionAggregate } from "../chat/ReactionsRow";
import type { TextBody } from "../appEvent/types";
import type { MessageMenuAction } from "../chat/MessageActionMenu";
import type { ChatMessageVariant } from "./ChatRoom.types";

export interface MessageRowProps {
	readonly message: Message;
	readonly variant?: ChatMessageVariant | undefined;
	readonly onReplyPress?: ((message: Message) => void) | undefined;
	readonly onMentionPress?: ((userId: string) => void) | undefined;
	/** Tapping the sender AVATAR opens the profile popover anchored to it. */
	readonly onAvatarPress?:
		| ((userId: string, anchorRef: React.RefObject<unknown>) => void)
		| undefined;
	readonly onRetryPress?: ((messageId: string) => void) | undefined;
	// Group chat sender label — Figma 3412:5207. When the host
	// passes a non-empty string and the bubble is received, the
	// header row above the bubble renders the sender name.
	readonly senderName?: string | undefined;
	/** Group-chat verified badge next to the sender name (Figma 7038:2148). */
	readonly senderVerified?: boolean | undefined;
	readonly senderAvatar?:
		| { readonly initials: string; readonly imageUri?: string }
		| undefined;
	/** Reserve left avatar column for stacked group bubbles. */
	readonly groupAvatarGutter?: boolean | undefined;
	// Thread "View Thread" affordance — Figma 2938:10511. The
	// MessageBubble / StackedMessageRow renders a `<ThreadPreviewCard>`
	// under the bubble. Optional participants/last-replier/preview
	// enrich the card.
	readonly threadAffordance?:
		| {
				readonly count: number;
				readonly unreadCount?: number | undefined;
				readonly onPress: () => void;
				readonly participants?:
					| ReadonlyArray<{ readonly initials: string; readonly imageUri?: string | undefined }>
					| undefined;
				readonly lastReplierName?: string | undefined;
				readonly preview?: string | undefined;
		  }
		| undefined;
	// Pinned message footer — Figma 3412:5095.
	readonly isPinned?: boolean | undefined;
	// Reflect saved/starred state in the action menu (Unsave / Unstar labels).
	readonly isSaved?: boolean | undefined;
	readonly isStarred?: boolean | undefined;
	// When true, this row is rendered INSIDE a thread panel — the action
	// menu hides "Reply in Thread" (no nested threads).
	readonly inThread?: boolean | undefined;
	// Layout density. "bubble" (default) → MessageBubble + tiles in
	// left/right rows; "stacked" → StackedMessageRow (Slack-style).
	readonly layoutMode?: "bubble" | "stacked" | undefined;
	// Stacked-mode: first message of a consecutive same-sender run
	// (draws the avatar + name/time header). Bubble-mode: the bubble
	// that gets the speech tail (last of a run).
	readonly showHeader?: boolean | undefined;
	readonly isLastOfGroup?: boolean | undefined;
	readonly isFirstOfGroup?: boolean | undefined;
	readonly onReplyQuotePress?: ((messageId: string) => void) | undefined;
	// Hover-actions strip — Figma 2804:27483. Pure pass-through; the
	// underlying `MessageBubble` renders the strip only when one of
	// these callbacks is supplied AND the user is hovering the row
	// (RN-Web + RN-macOS only).
	readonly onReactPress?: ((message: Message) => void) | undefined;
	readonly onReplyPressFromHover?: ((message: Message) => void) | undefined;
	/** Edit-from-hover (self + plain-text only — gated downstream). */
	readonly onEditPressFromHover?: ((message: Message) => void) | undefined;
	readonly onMorePress?: ((message: Message) => void) | undefined;
	// Aggregated reactions under the bubble — Figma 2804:27483.
	readonly reactions?: ReadonlyArray<ReactionAggregate> | undefined;
	readonly onToggleReaction?: ((emoji: string) => void) | undefined;
	// `(edited)` indicator. Only the timestamp is used downstream so
	// any truthy value renders the meta-row suffix.
	readonly editedAt?: number | undefined;
	// "Forwarded" indicator — threaded to the bubble / stacked row so a
	// forwarded copy shows the "Forwarded" label on both sender + recipient.
	readonly forwarded?: boolean | undefined;
	// Decoded `TextBody` (§2.2) when the host parsed the envelope and
	// wants rich-text rendering inside `MessageBubble`. Without this
	// the legacy plain-text + `@mention`-only path renders.
	readonly richBody?: TextBody | undefined;
	// Secondary action menu dispatch — Copy / Reply in Thread /
	// Forward / Pin / Remove.
	readonly onMenuAction?:
		| ((action: MessageMenuAction, message: Message) => void)
		| undefined;
	// In-chat games (types-and-schemas §2.9). The host resolves the live
	// folded session for this row's `sessionId` from `useGameSessions` and
	// passes it here, plus `onGameMove` to emit `com.ping.game_move`. The
	// tile is interactive + animated and updates in realtime as the peer
	// plays.
	readonly gameSession?: GameSession | undefined;
	readonly onGameMove?:
		| ((sessionId: string, moveIdx: number, payload: string) => void)
		| undefined;
	readonly gameOpponentName?: string | undefined;
	/**
	 * Resolves a display name for a reader userId. Supplied by the host only for
	 * group chats — enables the read-receipt tick to be tappable and open a popover
	 * listing every reader. When absent the tick is non-interactive.
	 */
	readonly resolveReaderName?: ((userId: string) => string | undefined) | undefined;
	/**
	 * Resolves an avatar image URI for a reader userId. Rendered in the
	 * "Read by" popover alongside the reader's name.
	 */
	readonly resolveReaderAvatar?: ((userId: string) => string | undefined) | undefined;
	/**
	 * Number of non-self members in the group. When provided, the green double-check
	 * only shows once ALL members have read (passed straight to MessageBubble and used
	 * to compute `isRead` for media tiles).
	 */
	readonly groupNonSelfMemberCount?: number | undefined;
}

export const MessageRow = React.memo<MessageRowProps>(
	({
		message,
		variant,
		onReplyPress,
		onMentionPress,
		onAvatarPress,
		onRetryPress,
		senderName,
		senderVerified,
		senderAvatar,
		groupAvatarGutter,
		threadAffordance,
		isPinned,
		isSaved,
		isStarred,
		inThread,
		layoutMode,
		showHeader,
		isLastOfGroup,
		isFirstOfGroup,
		onReplyQuotePress,
		onReactPress,
		onReplyPressFromHover,
		onEditPressFromHover,
		// `onMorePress` is retained on the props interface for
		// host-API compatibility but no longer consumed — the secondary
		// menu lives in `MessageHoverHost` and dispatches through
		// `onMenuAction` instead.
		reactions,
		onToggleReaction,
		editedAt,
		forwarded,
		richBody,
		onMenuAction,
		gameSession,
		onGameMove,
		gameOpponentName,
		resolveReaderName,
		resolveReaderAvatar,
		groupNonSelfMemberCount,
	}) => {
		const scheme = useColorScheme();
		// Derived read/delivered state — used by all tile renderers.
		// Group: green only when ALL non-self members have read.
		const readByCount = message.readBy?.length ?? 0;
		const isRead = groupNonSelfMemberCount !== undefined
			? readByCount >= groupNonSelfMemberCount && groupNonSelfMemberCount > 0
			: readByCount > 0;
		const isDelivered = message.deliveredAt !== undefined || isRead;
		// Fully-resolved reader list passed to media tiles (image/video/etc) so they
		// can self-contain the "Read by" AnchoredPopover without needing a shared ref.
		const tileReaders = React.useMemo(
			() =>
				message.isSelf && resolveReaderName !== undefined
					? (message.readBy ?? []).map((r) => {
							const avatarUri = resolveReaderAvatar?.(r.userId);
							return {
								userId: r.userId,
								name: resolveReaderName(r.userId) ?? 'Member',
								...(avatarUri !== undefined ? { avatarUri } : {}),
								timestamp: r.timestamp,
							};
						})
					: [],
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[message.isSelf, message.readBy, resolveReaderName, resolveReaderAvatar],
		);
		// Anchor for the avatar-tap profile popover on media-tile rows.
		const senderAvatarRef = React.useRef<View | null>(null);
		const avatarInteractive =
			onAvatarPress !== undefined || onMentionPress !== undefined;
		// Speech-tail color for media/voice/etc tiles when they're the LAST
		// of a sender run — own = brand blue, other = the dark/light
		// "other-bubble" surface (matches the tile fills).
		const tileTailColor = message.isSelf
			? "#537EFF"
			: scheme === "dark"
				? "#222429"
				: "#E5E7EB";
		// Stacked / Compact layout (Figma 2804:25812) — delegates the
		// entire row (text + tiles + reactions + footers) to the
		// de-bubbled StackedMessageRow. Handles its own event variant.
		if (layoutMode === "stacked") {
			return (
				<StackedMessageRow
					{...(forwarded === true ? { forwarded: true as const } : {})}
					message={message}
					showHeader={showHeader ?? true}
					{...(variant !== undefined ? { variant } : {})}
					{...(senderName !== undefined ? { senderName } : {})}
					{...(senderVerified !== undefined ? { senderVerified } : {})}
					{...(senderAvatar !== undefined ? { senderAvatar } : {})}
					{...(onReplyPress !== undefined ? { onReplyPress } : {})}
					{...(onMentionPress !== undefined ? { onMentionPress } : {})}
				{...(onAvatarPress !== undefined ? { onAvatarPress } : {})}
					{...(onRetryPress !== undefined ? { onRetryPress } : {})}
					{...(reactions !== undefined ? { reactions } : {})}
					{...(onToggleReaction !== undefined ? { onToggleReaction } : {})}
					{...(editedAt !== undefined ? { editedAt } : {})}
					{...(richBody !== undefined ? { richBody } : {})}
					{...(isPinned !== undefined ? { isPinned } : {})}
					{...(isSaved !== undefined ? { isSaved } : {})}
					{...(isStarred !== undefined ? { isStarred } : {})}
					{...(threadAffordance !== undefined ? { threadAffordance } : {})}
					{...(onReactPress !== undefined ? { onReactPress } : {})}
					{...(onReplyPressFromHover !== undefined
						? { onReplyPressFromHover }
						: {})}
					{...(onEditPressFromHover !== undefined &&
					variant === undefined &&
					message.isSelf
						? { onEditPressFromHover }
						: {})}
					{...(onMenuAction !== undefined ? { onMenuAction } : {})}
					{...(onReplyQuotePress !== undefined ? { onReplyQuotePress } : {})}
					{...(gameSession !== undefined ? { gameSession } : {})}
					{...(onGameMove !== undefined ? { onGameMove } : {})}
					{...(gameOpponentName !== undefined ? { gameOpponentName } : {})}
					{...(resolveReaderName !== undefined ? { resolveReaderName } : {})}
					{...(resolveReaderAvatar !== undefined ? { resolveReaderAvatar } : {})}
					{...(groupNonSelfMemberCount !== undefined ? { groupNonSelfMemberCount } : {})}
				/>
			);
		}

		// "event" variants render outside the bubble flow (centered
		// notification row) — there's no avatar / reply chrome.
		if (variant?.kind === "event") {
			return (
				<View accessibilityLabel="System event">
					<EventMessage
						kind={variant.type}
						variant={variant.variant}
						title={variant.title}
						detail={variant.detail ?? undefined}
						timestamp={new Date(message.timestamp)}
					/>
				</View>
			);
		}

		// Hover wrapper — every tile flavour shares the Figma
		// 2804:27483 hover action panel. Voice, image, doc, edit
		// tiles previously had no hover affordance; routing them
		// through `MessageHoverHost` unifies the UX with text bubbles.
		const wrapWithHover = (
			child: React.ReactNode,
			opts?: { suppressTail?: boolean },
		): JSX.Element => {
			const host = (
				<MessageHoverHost
					message={message}
					isSelf={message.isSelf}
					{...(onReactPress !== undefined ? { onReact: onReactPress } : {})}
					{...(onReplyPressFromHover !== undefined
						? { onReply: onReplyPressFromHover }
						: onReplyPress !== undefined
						? { onReply: onReplyPress }
						: {})}
					{...(onToggleReaction !== undefined ? { onToggleReaction } : {})}
					{...(onMenuAction !== undefined ? { onMenuAction } : {})}
					{...(isPinned !== undefined ? { isPinned } : {})}
					{...(isSaved !== undefined ? { isSaved } : {})}
					{...(isStarred !== undefined ? { isStarred } : {})}
					{...(inThread === true ? { hideReplyInThread: true } : {})}
					{...(isLastOfGroup !== false && opts?.suppressTail !== true
						? { tailColor: tileTailColor }
						: {})}
				>
					{child}
				</MessageHoverHost>
			);
			// Footer surfaces BELOW non-text tiles (image/voice/doc/poll/
			// game/location/gif): the aggregated reactions strip AND the
			// thread-preview affordance (Figma 2938:10511) — both were
			// previously text-bubble-only, so media replies in a thread had
			// no "N Replies" card and applied reactions were invisible.
			const hasReactions = reactions !== undefined && reactions.length > 0;
			const hasThread = threadAffordance !== undefined;
			const content =
				!hasReactions && !hasThread ? (
					host
				) : (
					<View className="flex flex-col">
						{host}
						{hasReactions ? (
							<ReactionsRow
								reactions={reactions}
								isSelf={message.isSelf}
								{...(onToggleReaction !== undefined ? { onToggleReaction } : {})}
							/>
						) : null}
						{hasThread ? (
							<View className="mt-1">
								<ThreadPreviewCard
									count={threadAffordance.count}
									onPress={threadAffordance.onPress}
									{...(threadAffordance.unreadCount !== undefined
										? { unreadCount: threadAffordance.unreadCount }
										: {})}
									{...(threadAffordance.participants !== undefined
										? { participants: threadAffordance.participants }
										: {})}
									{...(threadAffordance.lastReplierName !== undefined
										? { lastReplierName: threadAffordance.lastReplierName }
										: {})}
									{...(threadAffordance.preview !== undefined
										? { preview: threadAffordance.preview }
										: {})}
								/>
							</View>
						) : null}
					</View>
				);
			// Received media tiles get the sender's avatar at the bottom of
			// the LAST tile of a run (parity with text bubbles) — a 36px
			// gutter holds the avatar (last-of-group) or stays empty (earlier
			// tiles in the run) so the column stays aligned.
			if (message.isSelf || (senderAvatar === undefined && !groupAvatarGutter)) {
				return content;
			}
			return (
				// `min-w-0 shrink` lets this avatar+tile row shrink to the column
				// width (RN defaults flex children to flexShrink:0), so a fixed-width
				// media tile beside the avatar gutter fits a narrow thread panel.
				<View className="flex flex-row items-end gap-sm min-w-0 shrink">
					<View className="w-9 shrink-0 items-center justify-end pb-0.5">
						{senderAvatar !== undefined ? (
							<Pressable
								ref={senderAvatarRef}
								onPress={
									onAvatarPress !== undefined
										? () => onAvatarPress(message.senderId, senderAvatarRef)
										: onMentionPress !== undefined
											? () => onMentionPress(message.senderId)
											: undefined
								}
								accessibilityRole={avatarInteractive ? "button" : undefined}
								accessibilityLabel={avatarInteractive ? "Open profile" : undefined}
							>
								<Avatar
									initials={senderAvatar.initials}
									{...(senderAvatar.imageUri !== undefined
										? { imageUri: senderAvatar.imageUri }
										: {})}
									size="small"
									shape="rounded"
								/>
							</Pressable>
						) : null}
					</View>
					{/* `min-w-0 flex-1` lets a fixed-width media tile (poll 280px,
					    image/video cards) SHRINK to the available width beside the
					    avatar gutter — the tiles cap at `max-w-full`, so this is a
					    no-op in the wide main chat but keeps them inside the narrow
					    thread panel instead of overflowing it. */}
					<View className="min-w-0 flex-1">{content}</View>
				</View>
			);
		};

		// "voice" — wrap to keep the row alignment consistent with the
		// plain bubble row (self → right, other → left). When the host
		// supplies a `resolveVoiceSource`, the bubble swaps to the
		// shared playback hook (`useVoicePlayback`) — `progress`,
		// `waveform`, and `onTogglePlay` from the variant become
		// fall-back values the hook overrides once a driver is wired.
		if (variant?.kind === "voice") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<VoiceMessageBubble
							state={variant.state}
							durationMs={variant.durationMs}
							waveform={variant.waveform}
							progress={variant.progress}
							timestamp={new Date(message.timestamp)}
							isSelf={message.isSelf}
							delivered={isDelivered}
							read={isRead}
							onTogglePlay={variant.onTogglePlay}
							onRetry={variant.onRetry}
							{...(variant.transcript !== undefined
								? { transcript: variant.transcript }
								: {})}
							{...(variant.onPlaybackError !== undefined
								? { onPlaybackError: variant.onPlaybackError }
								: {})}
							{...(variant.resolveVoiceSource !== undefined
								? { message, resolveVoiceSource: variant.resolveVoiceSource }
								: {})}
							{...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
						/>,
					)}
				</View>
			);
		}

		// "image" — Figma `3412:5288` card sits 300w x ~399h with
		// padding 2/2/2/2 r=16. The card primitive owns its own
		// dimensions; we just align it to the message author side.
		if (variant?.kind === "image") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<ImageAttachmentCard
							state={variant.state}
							aspectRatio={variant.aspectRatio}
							blurhash={variant.blurhash}
							imageUri={variant.imageUri}
							progress={variant.progress}
							caption={variant.caption}
							isSelf={message.isSelf}
							{...(variant.isSticker === true ? { isSticker: true } : {})}
							{...(isFirstOfGroup !== undefined ? { isFirstOfGroup } : {})}
							{...(isLastOfGroup !== undefined ? { isLastOfGroup } : {})}
							timeLabel={formatClockTime(new Date(message.timestamp))}
							delivered={isDelivered}
							read={isRead}
							onTapOpen={variant.onTapOpen}
							onRetry={variant.onRetry}
							{...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
						/>,
					)}
				</View>
			);
		}

		// "video" — full inline player on web (poster + play/pause, seek,
		// mute, PiP, fullscreen, download); poster + open on native.
		if (variant?.kind === "video") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<VideoAttachmentCard
							state={variant.state}
							aspectRatio={variant.aspectRatio}
							blurhash={variant.blurhash}
							posterUri={variant.posterUri}
							videoUri={variant.videoUri}
							mimeType={variant.mimeType}
							durationLabel={variant.durationLabel}
							caption={variant.caption}
							progress={variant.progress}
							downloading={variant.downloading}
							downloadProgress={variant.downloadProgress}
							isSelf={message.isSelf}
							{...(isFirstOfGroup !== undefined ? { isFirstOfGroup } : {})}
							{...(isLastOfGroup !== undefined ? { isLastOfGroup } : {})}
							timeLabel={formatClockTime(new Date(message.timestamp))}
							delivered={isDelivered}
							read={isRead}
							onTapOpen={variant.onTapOpen}
							onDownload={variant.onDownload}
							onFullscreen={variant.onFullscreen}
							onRetry={variant.onRetry}
							{...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
						/>,
					)}
				</View>
			);
		}

		// "linkPreview" — smart unfurl card (image banner + title + desc +
		// domain). Left-anchored for received, right for self.
		if (variant?.kind === "linkPreview") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<LinkPreviewCard
							url={variant.url}
							title={variant.title}
							description={variant.description}
							imageUri={variant.imageUri}
							domain={variant.domain}
							isSelf={message.isSelf}
							onOpen={variant.onOpen}
						/>,
					)}
				</View>
			);
		}

		// "doc" — Figma `3412:4897` card 300w x 254h padding 4/4/4/4
		// r=16, with PDF icon chip 43x21 r=1000.
		if (variant?.kind === "doc") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<DocAttachmentCard
							state={variant.state}
							filename={variant.filename}
							mimeType={variant.mimeType}
							byteSize={variant.byteSize}
							pageCount={variant.pageCount}
							previewUri={variant.previewUri}
							progress={variant.progress}
							isSelf={message.isSelf}
							timeLabel={formatClockTime(new Date(message.timestamp))}
							delivered={isDelivered}
							read={isRead}
							onTapPreview={variant.onTapPreview}
							onRetry={variant.onRetry}
							{...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
						/>,
					)}
				</View>
			);
		}

		// "location" — Figma share-location result card with a static
		// map preview (LocationAttachmentCard).
		if (variant?.kind === "location") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<LocationAttachmentCard
							lat={variant.lat}
							lng={variant.lng}
							{...(variant.label !== undefined ? { label: variant.label } : {})}
							isSelf={message.isSelf}
							{...(variant.onOpen !== undefined ? { onOpen: variant.onOpen } : {})}
							{...(variant.live !== undefined ? { live: variant.live } : {})}
							{...(variant.liveUntilTs !== undefined
								? { liveUntilTs: variant.liveUntilTs }
								: {})}
							{...(variant.sharerAvatarUrl !== undefined
								? { sharerAvatarUrl: variant.sharerAvatarUrl }
								: {})}
							timestamp={message.timestamp}
							delivered={isDelivered}
							read={isRead}
							{...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
						/>,
					)}
				</View>
			);
		}

		// "poll" — in-chat poll result card with live vote tally.
		if (variant?.kind === "poll") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<PollView
							poll={variant.poll}
							counts={variant.counts}
							{...(variant.myVote !== undefined ? { myVote: variant.myVote } : {})}
							{...(variant.myVotes !== undefined ? { myVotes: variant.myVotes } : {})}
							total={variant.total}
							onVote={variant.onVote}
							{...(variant.votersByOption !== undefined
								? { votersByOption: variant.votersByOption }
								: {})}
							{...(variant.onViewVotes !== undefined
								? { onViewVotes: variant.onViewVotes }
								: {})}
							isSelf={message.isSelf}
							delivered={isDelivered}
						/>,
					)}
				</View>
			);
		}

		// "meeting" — in-chat meeting card (`com.ping.meeting`).
		if (variant?.kind === "meeting") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<MeetingView
							content={variant.content}
							counts={variant.counts}
							{...(variant.myRsvp !== undefined ? { myRsvp: variant.myRsvp } : {})}
							{...(variant.onRsvp !== undefined ? { onRsvp: variant.onRsvp } : {})}
							{...(variant.onOpenLink !== undefined ? { onOpenLink: variant.onOpenLink } : {})}
							{...(variant.onCopyLink !== undefined ? { onCopyLink: variant.onCopyLink } : {})}
							{...(variant.onEdit !== undefined ? { onEdit: variant.onEdit } : {})}
							{...(variant.onCancelMeeting !== undefined
								? { onCancelMeeting: variant.onCancelMeeting }
								: {})}
							{...(variant.onShowResponses !== undefined
								? { onShowResponses: variant.onShowResponses }
								: {})}
							{...(variant.onJoinCall !== undefined ? { onJoinCall: variant.onJoinCall } : {})}
							isSelf={message.isSelf}
							delivered={isDelivered}
							timeLabel={formatClockTime(new Date(message.timestamp))}
						/>,
					)}
				</View>
			);
		}

		// "call" — in-chat call tile. Room-owned event: centred in the timeline,
		// not aligned to the sender side. No avatar chrome (suppressed upstream).
		if (variant?.kind === "call") {
			return (
				<View className="flex flex-row justify-start">
					{wrapWithHover(
						<CallNotificationCard
							status={variant.status}
							durationLabel={variant.durationLabel}
							timeLabel={formatClockTime(new Date(message.timestamp))}
							delivered={isDelivered}
							{...(variant.callState !== undefined
								? { state: variant.callState }
								: {})}
							{...(variant.startedAtMs !== undefined
								? { startedAtMs: variant.startedAtMs }
								: {})}
							{...(variant.participants !== undefined
								? { participants: variant.participants }
								: {})}
							{...(variant.extraCount !== undefined
								? { extraCount: variant.extraCount }
								: {})}
							{...(variant.joined !== undefined
								? { joined: variant.joined }
								: {})}
							{...(variant.onJoinLeave !== undefined
								? { onJoinLeave: variant.onJoinLeave }
								: {})}
							{...(variant.notes !== undefined
								? { notes: variant.notes }
								: {})}
							{...(variant.onOpenCallThread !== undefined
								? { onOpenCallThread: variant.onOpenCallThread }
								: {})}
							{...(variant.callThreadHasMessages !== undefined
								? { callThreadHasMessages: variant.callThreadHasMessages }
								: {})}
							{...(variant.actionable !== undefined
								? { actionable: variant.actionable }
								: {})}
						/>,
						{ suppressTail: true },
					)}
				</View>
			);
		}

		// "game" — timeline card for a game session (types-and-schemas
		// §2.9). Tapping opens the full game via the host.
		if (variant?.kind === "game") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<GameTile
							gameKind={variant.gameKind}
							sessionId={variant.sessionId}
							isCreator={message.isSelf}
							session={gameSession}
							{...(onGameMove !== undefined ? { onMove: onGameMove } : {})}
							selfName="You"
							{...(gameOpponentName !== undefined
								? { opponentName: gameOpponentName }
								: {})}
						/>,
					)}
				</View>
			);
		}

		// "edit" — render the edit bubble variant. Body comes from
		// `message.body`, `editedAt` from the variant payload.
		if (variant?.kind === "edit") {
			return (
				<View
					className={`flex flex-row ${
						message.isSelf ? "justify-end" : "justify-start"
					}`}
				>
					{wrapWithHover(
						<EditMessageBubble
							variant={variant.variant}
							isSelf={message.isSelf}
							body={message.body}
							editedAt={new Date(variant.editedAt)}
						/>,
					)}
				</View>
			);
		}

		// Default: plain text bubble. Renders `MessageBubble` directly
		// (the Figma 2804:27483 hover panel is embedded inside its
		// `bubbleHoverHost` — a `position: relative` container sized
		// to the bubble's pixel rect). The bubble owns its own popover.
		return (
			<MessageBubble
				{...(forwarded === true ? { forwarded: true as const } : {})}
				message={message}
				{...(onReplyPress !== undefined ? { onReplyPress } : {})}
				{...(onMentionPress !== undefined ? { onMentionPress } : {})}
				{...(onAvatarPress !== undefined ? { onAvatarPress } : {})}
				{...(onRetryPress !== undefined ? { onRetryPress } : {})}
				{...(senderName !== undefined ? { senderName } : {})}
				{...(senderVerified !== undefined ? { senderVerified } : {})}
				{...(senderAvatar !== undefined ? { senderAvatar } : {})}
				{...(groupAvatarGutter === true ? { groupAvatarGutter: true } : {})}
				{...(threadAffordance !== undefined ? { threadAffordance } : {})}
				{...(isPinned !== undefined ? { isPinned } : {})}
				{...(isSaved !== undefined ? { isSaved } : {})}
				{...(isStarred !== undefined ? { isStarred } : {})}
				{...(isLastOfGroup !== undefined ? { isLastOfGroup } : {})}
				{...(isFirstOfGroup !== undefined ? { isFirstOfGroup } : {})}
				{...(onReplyQuotePress !== undefined ? { onReplyQuotePress } : {})}
				{...(inThread === true ? { inThread: true } : {})}
				{...(reactions !== undefined ? { reactions } : {})}
				{...(onToggleReaction !== undefined ? { onToggleReaction } : {})}
				{...(editedAt !== undefined ? { editedAt } : {})}
				{...(richBody !== undefined ? { richBody } : {})}
				{...(onReactPress !== undefined ? { onReact: onReactPress } : {})}
				{...(onReplyPressFromHover !== undefined
					? { onReplyFromHover: onReplyPressFromHover }
					: onReplyPress !== undefined
					? { onReplyFromHover: onReplyPress }
					: {})}
				{...(onEditPressFromHover !== undefined && message.isSelf
					? { onEditFromHover: onEditPressFromHover }
					: {})}
				{...(onMenuAction !== undefined ? { onMenuAction } : {})}
				{...(resolveReaderName !== undefined ? { resolveReaderName } : {})}
				{...(resolveReaderAvatar !== undefined ? { resolveReaderAvatar } : {})}
				{...(groupNonSelfMemberCount !== undefined ? { groupNonSelfMemberCount } : {})}
			/>
		);
	},
);

MessageRow.displayName = "MessageRow";
